import BASE_URL from "../../config/api";
import { useState, useEffect } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Upload, Form } from "antd";
import { BsArrowLeft, BsCloudUpload, BsCheckCircle } from "react-icons/bs";
const { Dragger } = Upload;

const ImageField = ({ label, currentUrl, fileName, onUpload, uploadAction }) => (
  <div>
    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">{label}</p>
    {currentUrl && (
      <div className="mb-2 rounded-xl overflow-hidden h-40 bg-gray-100">
        <img src={currentUrl} alt={label} className="w-full h-full object-cover" />
      </div>
    )}
    <Dragger
      name="file"
      maxCount={1}
      action={uploadAction}
      headers={{ Authorization: `Bearer ${localStorage.getItem("userToken")}` }}
      onChange={onUpload}
      showUploadList={false}
      className="!border-dashed !border-gray-300 !rounded-xl !bg-gray-50 hover:!border-[#1877F2] transition-colors"
    >
      {fileName ? (
        <div className="py-4 flex flex-col items-center gap-1.5">
          <BsCheckCircle size={22} className="text-green-500" />
          <p className="text-xs font-semibold text-gray-700">{fileName}</p>
          <p className="text-[10px] text-gray-400">Click to replace</p>
        </div>
      ) : (
        <div className="py-5 flex flex-col items-center gap-1.5">
          <BsCloudUpload size={22} className="text-gray-400" />
          <p className="text-xs text-gray-500">{currentUrl ? "Click to replace image" : "Upload image"}</p>
        </div>
      )}
    </Dragger>
  </div>
);

const EditDrawing = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formFields, setFormFields] = useState({
    description: "",
    price: "",
    type: "",
    category: "",
    refNo: "",
    frontElevation: "",
    rightElevation: "",
    leftElevation: "",
  });
  const [imageFiles, setImageFiles] = useState({
    front: "",
    right: "",
    left: "",
  });

  useEffect(() => {
    const load = async () => {
      try {
        const response = await axios.get(`${BASE_URL}/drawing/get-drawings/${id}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("userToken")}` },
        });
        const d = response.data.data;
        setFormFields({
          description: d.description ?? "",
          price: d.price ?? "",
          type: d.type ?? "",
          category: d.category ?? "",
          refNo: d.refNo ?? "",
          frontElevation: d.frontElevation ?? "",
          rightElevation: d.rightElevation ?? "",
          leftElevation: d.leftElevation ?? "",
        });
      } catch {
        toast.error("Failed to load drawing data");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  const handleFieldChange = (e) => {
    const { name, value } = e.target;
    setFormFields((prev) => ({ ...prev, [name]: value }));
  };

  const makeImageUploadHandler = (key, fieldKey) => (file) => {
    if (file.file.status === "done") {
      const response = file.file.response;
      if (response?.success) {
        const url = response.data[0].bucket.data.url;
        setFormFields((prev) => ({ ...prev, [fieldKey]: url }));
        setImageFiles((prev) => ({ ...prev, [key]: file.file.name }));
        toast.success("Image uploaded");
      } else {
        toast.error("Image upload failed");
      }
    } else if (file.file.status === "error") {
      toast.error("Upload failed — network issue");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const response = await axios.post(`${BASE_URL}/drawing/edit-drawing/${id}`, formFields, {
        headers: { Authorization: `Bearer ${localStorage.getItem("userToken")}` },
      });
      if (response.status === 200) {
        toast.success("Drawing updated successfully");
        setTimeout(() => navigate("/all-drawings"), 1500);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Update failed");
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputClass = "w-full px-4 py-3 rounded-xl border border-gray-200 bg-gray-50 text-sm focus:outline-none focus:ring-2 focus:ring-[#1877F2]/30 focus:border-[#1877F2] transition-colors";
  const selectClass = `${inputClass} appearance-none`;
  const labelClass = "block text-sm font-semibold text-gray-700 mb-1.5";

  if (loading) {
    return (
      <div className="max-w-3xl animate-pulse space-y-6">
        <div className="h-5 bg-gray-200 rounded w-32" />
        <div className="bg-white rounded-2xl border border-gray-100 p-6 space-y-4">
          <div className="grid grid-cols-3 gap-4">
            {[0,1,2].map(i => <div key={i} className="h-40 bg-gray-200 rounded-xl" />)}
          </div>
        </div>
        <div className="bg-white rounded-2xl border border-gray-100 p-6 space-y-4">
          {[0,1,2,3].map(i => <div key={i} className="h-12 bg-gray-200 rounded-xl" />)}
        </div>
      </div>
    );
  }

  return (
    <div>
      <ToastContainer />

      <div className="mb-6">
        <Link
          to="/all-drawings"
          className="inline-flex items-center gap-2 text-gray-500 hover:text-gray-800 text-sm font-medium transition-colors"
        >
          <BsArrowLeft size={16} /> Back to Drawings
        </Link>
      </div>

      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Edit Drawing</h1>
        <p className="text-gray-500 text-sm mt-1">Update any details or replace elevation images.</p>
      </div>

      <form onSubmit={handleSubmit} className="max-w-3xl space-y-6">

        {/* Elevation images */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <h2 className="text-sm font-bold text-gray-800 uppercase tracking-wide mb-5">Elevation Images</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <ImageField
              label="Front Elevation"
              currentUrl={formFields.frontElevation}
              fileName={imageFiles.front}
              uploadAction={`${BASE_URL}/bucket/upload`}
              onUpload={makeImageUploadHandler("front", "frontElevation")}
            />
            <ImageField
              label="Right Elevation"
              currentUrl={formFields.rightElevation}
              fileName={imageFiles.right}
              uploadAction={`${BASE_URL}/bucket/upload`}
              onUpload={makeImageUploadHandler("right", "rightElevation")}
            />
            <ImageField
              label="Left Elevation"
              currentUrl={formFields.leftElevation}
              fileName={imageFiles.left}
              uploadAction={`${BASE_URL}/bucket/upload`}
              onUpload={makeImageUploadHandler("left", "leftElevation")}
            />
          </div>
        </div>

        {/* Plan details */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <h2 className="text-sm font-bold text-gray-800 uppercase tracking-wide mb-5">Plan Details</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className={labelClass}>Type</label>
              <select name="type" value={formFields.type} onChange={handleFieldChange} className={selectClass}>
                <option value="">Select type</option>
                <option value="bungalow">Bungalow</option>
                <option value="duplex">Duplex</option>
                <option value="flat">Flat</option>
                <option value="nonResidential">Non-Residential</option>
              </select>
            </div>
            <div>
              <label className={labelClass}>Category</label>
              <select name="category" value={formFields.category} onChange={handleFieldChange} className={selectClass}>
                <option value="">Select category</option>
                <option value="2-bedroom">2 Bedroom</option>
                <option value="3-bedroom">3 Bedroom</option>
                <option value="4-bedroom">4 Bedroom</option>
                <option value="5-bedroom">5 Bedroom</option>
                <option value="6-bedroom">6+ Bedroom</option>
                <option value="nonResidential">Non-Residential</option>
              </select>
            </div>
            <div className="md:col-span-2">
              <label className={labelClass}>Description</label>
              <input
                required
                type="text"
                name="description"
                value={formFields.description}
                onChange={handleFieldChange}
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Reference Number</label>
              <input
                type="text"
                name="refNo"
                value={formFields.refNo}
                onChange={handleFieldChange}
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Price (₦)</label>
              <input
                required
                type="text"
                name="price"
                value={formFields.price}
                onChange={handleFieldChange}
                className={inputClass}
              />
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3">
          <button
            type="submit"
            disabled={isSubmitting}
            className="bg-[#1877F2] hover:bg-[#0e5fc7] text-white font-bold px-8 py-3 rounded-xl transition-colors duration-200 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {isSubmitting ? "Saving…" : "Save Changes"}
          </button>
          <Link
            to="/all-drawings"
            className="px-8 py-3 rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-50 font-semibold text-sm transition-colors duration-200 flex items-center"
          >
            Cancel
          </Link>
        </div>
      </form>
    </div>
  );
};

export default EditDrawing;
